/**
 * @file api.ts
 * @brief Shared TypeScript + Zod schemas describing backend API payloads.
 *
 * @details
 * These types mirror the response shapes produced by the existing
 * `BACKEND/src/features/auth/service.js` (loginUser) and the admin/company
 * routes. Keeping them in one place gives us:
 * - End-to-end type safety in queries / mutations.
 * - Runtime validation via Zod, so backend drift surfaces in the UI as a
 *   loud, actionable error rather than a silent crash.
 *
 * @dependencies
 * - zod: schema declaration + parsing
 *
 * @references
 * - BACKEND/src/features/auth/service.js   (login response shape)
 * - BACKEND/src/features/admin/service.js  (admin payloads)
 * - BACKEND/src/features/company/routes.js (company payloads)
 *
 * @author DES
 * @date 26-05-2026
 */

import { z } from "zod";

// ----------------------------------------------------------------------
// Roles & status enums
// Mirror values defined in BACKEND/src/db/schema.sql::users.role / status.
// ----------------------------------------------------------------------
export const UserRoleSchema = z.enum(["user", "admin", "superadmin", "company_admin"]);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const UserStatusSchema = z.enum(["active", "suspended", "deleted"]);
export type UserStatus = z.infer<typeof UserStatusSchema>;

// ----------------------------------------------------------------------
// Authenticated user (returned by /api/auth/login and /api/auth/me)
// `passthrough()` is intentional — backend may add fields (profile image,
// billing context) we don't strictly need on the panel; we don't want
// validation to fail just because new optional fields appear.
// ----------------------------------------------------------------------
export const AuthUserSchema = z
  .object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().optional(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    phoneNumber: z.string().nullable().optional(),
    role: UserRoleSchema.default("user"),
    company_id: z.string().nullable().optional(),
    companyId: z.string().nullable().optional(),
    company_name: z.string().nullable().optional(),
    profileImageUrl: z.string().nullable().optional(),
    status: UserStatusSchema.optional(),
    // Terms + Privacy gate (GDPR, 06-10-2026); missing = not asked.
    needsTermsAcceptance: z.boolean().optional(),
    termsVersion: z.string().nullable().optional(),
  })
  .passthrough();

export type AuthUser = z.infer<typeof AuthUserSchema>;

// ----------------------------------------------------------------------
// Effective feature access (GET /api/auth/me + /api/auth/entitlements)
// Admins/individuals → all ON; company employees → company toggles.
// ----------------------------------------------------------------------
export const EntitlementsSchema = z.object({
  interpretation: z.boolean(),
  notes: z.boolean(),
  boardroom: z.boolean(),
  // Z1 (27-10-2026): false = the company has not allowed Zapier (admins too).
  // Absent on an older backend: treated as allowed, the server still decides.
  zapier: z.boolean().optional(),
  // M1 (29-10-2026): the same for Make (company switch `make`).
  make: z.boolean().optional(),
});
export type Entitlements = z.infer<typeof EntitlementsSchema>;

export const ALL_ENTITLEMENTS_ON: Entitlements = {
  interpretation: true,
  notes: true,
  boardroom: true,
};

// ----------------------------------------------------------------------
// /api/auth/login response
// Backend (auth/routes.js::POST /login) wraps the service result with
// `{ success: true, ...result }` where result = { token, user }.
// ----------------------------------------------------------------------
export const LoginResponseSchema = z.object({
  success: z.literal(true),
  token: z.string().min(10),
  user: AuthUserSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ----------------------------------------------------------------------
// /api/auth/me response
// ----------------------------------------------------------------------
export const MeResponseSchema = z.object({
  success: z.literal(true),
  user: AuthUserSchema,
  entitlements: EntitlementsSchema.optional(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const EntitlementsResponseSchema = z.object({
  success: z.literal(true),
  entitlements: EntitlementsSchema,
});


// ----------------------------------------------------------------------
// Generic error envelope returned by most backend routes.
// ----------------------------------------------------------------------
export const ApiErrorSchema = z.object({
  success: z.literal(false).optional(),
  message: z.string().optional(),
  code: z.string().optional(),
  error: z.string().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;
